/**
 * GrantDrawer — 440px right drawer. Toggle which SK products may use the
 * connection; shows conflict ("now: X") when another connector owns a route.
 */
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../theme';
import { Drawer, Toggle } from '../components';
import { Close } from '../icons';
import { useBridge } from '../store/bridgeStore';
import { allInts, nameOf } from '../domain/catalog';
// FE↔BE integration (Round 3): map FE bindings to canonical routes so bindings
// catalog v1 can't represent (messaging/payments/tickets) render disabled
// instead of firing a request the backend would reject.
import { mapBinding } from '../api/mappers';
import { USE_BACKEND } from '../api/config';

export default function GrantDrawer() {
  const { state, actions } = useBridge();
  if (!state.grantId) return null;
  const ints = allInts(state.adapters, state.metaOverrides);
  const g = ints.find(i => i.id === state.grantId);
  if (!g) return null;
  const c = state.conn[g.id] || { vendorVersion: null };

  return (
    <Drawer onClose={actions.closeGrant}>
      <View style={styles.header}>
        <View style={[styles.avatar, { backgroundColor: g.color } as any]}><Text style={styles.avatarText}>{g.initials}</Text></View>
        <View style={{ flex: 1 }}>
          <Text style={styles.name}>{g.name}</Text>
          <Text style={styles.sub}>{'connected · ' + (c.vendorVersion || g.versions[0])}</Text>
        </View>
        <Pressable onPress={actions.closeGrant}><Close size={18} color={colors.ink3} /></Pressable>
      </View>

      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20 }}>
        <Text style={styles.intro}>Choose which SK products are allowed to use this connection.</Text>
        <View style={{ gap: 10 }}>
          {g.products.map((p, i) => {
            const key = p.key + ':' + p.dataType;
            const owner = state.routes[key];
            const on = owner === g.id;
            const conflictName = owner && owner !== g.id ? nameOf(owner, ints) : null;
            // On the backend, a binding is only grantable if it maps to a
            // canonical entity in catalog v1. messaging/payments/tickets don't,
            // so we disable the toggle and explain why (rather than let the user
            // fire a request the BE would reject). Mock mode keeps all enabled.
            const grantable = !USE_BACKEND || mapBinding({ key: p.key, dataType: p.dataType }) != null;
            return (
              <View key={i} style={[styles.row, !grantable && styles.rowDisabled]}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.product}>{p.key}</Text>
                  <Text style={styles.rowSub}>{'Let ' + p.key + ' use ' + g.name + ' for ' + p.dataType}</Text>
                  {conflictName && <Text style={styles.conflict}>{'now: ' + conflictName}</Text>}
                  {!grantable && <Text style={styles.notSupported}>Not in catalog v1 yet</Text>}
                </View>
                <Toggle on={on} disabled={!grantable} onToggle={() => actions.toggleGrant(g.id, p.key, p.dataType, on)} />
              </View>
            );
          })}
        </View>
        <View style={styles.infoBox}>
          <Text style={styles.infoText}>
            A data type can only have one owner per product. Turning one on where another already handles it will ask you to switch.
          </Text>
        </View>
      </ScrollView>
    </Drawer>
  );
}

const styles = StyleSheet.create({
  header: { padding: 18, paddingHorizontal: 20, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', alignItems: 'center', gap: 12 },
  avatar: { width: 38, height: 38, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  avatarText: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.sm, color: '#fff' },
  name: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.lg, color: colors.ink },
  sub: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink3 },
  intro: { fontFamily: fontFamily.sans, fontSize: fontSize.md, color: colors.ink2, marginBottom: 16, lineHeight: 19.5 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, backgroundColor: colors.paper2 },
  product: { fontFamily: fontFamily.sans, fontWeight: fontWeight.medium, fontSize: fontSize.md, color: colors.ink },
  rowSub: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, marginTop: 1 },
  conflict: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.warn, marginTop: 4 },
  // FE↔BE integration (Round 3): styling for bindings not yet representable in
  // catalog v1 — a dimmed row + a small explanatory note.
  rowDisabled: { opacity: 0.55 },
  notSupported: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink3, marginTop: 4 },
  infoBox: { flexDirection: 'row', gap: 8, marginTop: 18, padding: 12, paddingHorizontal: 14, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper2, borderRadius: radii.r3 },
  infoText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, lineHeight: 18 },
});
